import { Capacitor, registerPlugin } from "@capacitor/core";

// Bridge to the native AudioSessionPlugin (ios/App/App/AudioSessionPlugin.swift).
//
// WHY: on iOS/WKWebView the JS layer cannot change the AVAudioSession category.
// getUserMedia (Whisper STT) forces a record/voice-processing mode whose AGC
// ducks media output and leaves it ducked after the mic releases — so the 2nd+
// TTS reply plays at minimum volume. Setting the category natively is the only
// real fix. This module is the thin TS shim over that plugin.
//
// O plugin é iOS-ONLY POR DESENHO: o problema que ele resolve é a
// AVAudioSession do WKWebView, um conceito que o WebView do Android não tem —
// e o lado nativo só existe no iOS (ios/App/App/AudioSessionPlugin.swift).
// Por isso o guard abaixo é de PLATAFORMA (getPlatform() === "ios"), não de
// "é nativo": no Android o proxy do Capacitor lança "not implemented" em toda
// chamada, o catch degradava certinho… e o console.error virava evento
// (integração de 29/07) — ~800 usuários/semana de ruído no Sentry por uma
// falha 100% esperada (task d410d610). Android e web são no-op limpo: sem
// try, sem catch, sem console.
//
// ⚠️ LOTE 5 (06/09/2026) — ATÉ ESTE LOTE NADA DISSO RODAVA. O plugin existia
// no disco e não estava no Compile Sources do target App (o App.xcodeproj usa
// referências explícitas, não grupos sincronizados), então as três funções
// abaixo rejeitavam com `UNIMPLEMENTED` em TODO iPhone: Sentry
// VYV-FRONTEND-50, 257 eventos / 140 usuários entre 31/07 e 06/09. Os
// console.error daqui não eram ruído — eram o alarme certo, tocando o tempo
// todo, sobre um conserto que nunca tinha entrado no binário.
interface AudioSessionPlugin {
  // Full-volume playback (.playback) — call before each TTS and at end of STT.
  setPlaybackMode(): Promise<Record<string, unknown>>;
  // Capture (.playAndRecord, defaultToSpeaker) — call when STT recording starts.
  setRecordMode(): Promise<Record<string, unknown>>;
  // Full-duplex (.voiceChat) — o LIVE, desde o LOTE 5.
  setVoiceChatMode(): Promise<Record<string, unknown>>;
}

/**
 * O que a AVAudioSession REALMENTE negociou depois do setActive (LOTE 5).
 *
 * `sampleRate` é a taxa efetiva do hardware; `preferredSampleRate` é o que o
 * plugin PEDIU (hoje 48 kHz só no modo playback). As duas juntas são o que
 * permite decidir sobre esse 48 000 sem palpite — e `outputRoute` é o que
 * responde "saiu pelo alto-falante ou pelo receptor?", o sintoma que o
 * .defaultToSpeaker deveria matar.
 *
 * Tudo opcional por desenho: um binário antigo (ou o de amanhã, se alguém
 * mexer no Swift) pode resolver só `{ mode }`, e ler campo que não veio não
 * pode virar exceção no meio de uma conversa.
 */
export interface NativeAudioSessionInfo {
  mode: string | null;
  sampleRate: number | null;
  preferredSampleRate: number | null;
  ioBufferDuration: number | null;
  outputLatency: number | null;
  outputRoute: string | null;
}

const AudioSession = registerPlugin<AudioSessionPlugin>("AudioSession");

const isIos = (): boolean => Capacitor.getPlatform() === "ios";

/** Número finito, ou null. Ponte nativa devolve `any`; NaN/string não é medida. */
function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Normaliza a resposta da ponte. Nunca lança: campo ausente vira null. */
export function toSessionInfo(raw: unknown): NativeAudioSessionInfo {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    mode: typeof r.mode === "string" ? r.mode : null,
    sampleRate: num(r.sampleRate),
    preferredSampleRate: num(r.preferredSampleRate),
    ioBufferDuration: num(r.ioBufferDuration),
    outputLatency: num(r.outputLatency),
    outputRoute: typeof r.outputRoute === "string" ? r.outputRoute : null,
  };
}

// Flip the native audio session to full-volume playback. No-op fora do iOS.
// Non-blocking (a session hiccup must never block a TTS reply) but NOT silent
// NO iOS: lá o plugin existe e uma rejeição é sinal real — if the native
// restore rejects, surface it loudly so a device repro of the "2nd turn
// low/telephone" bug shows whether the AVAudioSession restore failed.
async function applyPlaybackMode(): Promise<NativeAudioSessionInfo | null> {
  try {
    return toSessionInfo(await AudioSession.setPlaybackMode());
  } catch (err) {
    console.error(
      "[nativeAudioSession] setPlaybackMode REJECTED — AVAudioSession restore failed; TTS may play low/band-limited:",
      err,
    );
    return null;
  }
}

// Flip the native audio session to record mode for STT capture. No-op fora do iOS.
async function applyRecordMode(): Promise<NativeAudioSessionInfo | null> {
  try {
    return toSessionInfo(await AudioSession.setRecordMode());
  } catch (err) {
    console.error("[nativeAudioSession] setRecordMode failed", err);
    return null;
  }
}

// O LIVE full-duplex (.voiceChat + AEC do sistema): chamado por useMirrorLive
// ao ENTRAR, e desfeito com setPlaybackMode ao sair. No-op fora do iOS.
async function applyVoiceChatMode(): Promise<NativeAudioSessionInfo | null> {
  try {
    return toSessionInfo(await AudioSession.setVoiceChatMode());
  } catch (err) {
    console.error("[nativeAudioSession] setVoiceChatMode failed", err);
    return null;
  }
}

/** All iOS category changes use one queue; bridge operations cannot be aborted. */
let audioModeQueue: Promise<unknown> = Promise.resolve();
function queueMode(change: () => Promise<NativeAudioSessionInfo | null>): Promise<NativeAudioSessionInfo | null> {
  const next = audioModeQueue.then(change, change);
  audioModeQueue = next.catch(() => {});
  return next;
}

export interface NativeVoiceChatAudioSession {
  readonly ready: Promise<NativeAudioSessionInfo | null>;
  release(): Promise<NativeAudioSessionInfo | null>;
}
let activeVoiceChat: NativeVoiceChatAudioSession | null = null;

export function setPlaybackMode(): Promise<NativeAudioSessionInfo | null> {
  if (!isIos()) return Promise.resolve(null);
  // A delayed dictation/TTS completion must never deactivate an active LIVE.
  return queueMode(() => activeVoiceChat ? Promise.resolve(null) : applyPlaybackMode());
}

export function setRecordMode(): Promise<NativeAudioSessionInfo | null> {
  if (!isIos()) return Promise.resolve(null);
  if (activeVoiceChat) return Promise.reject(new Error("native_audio_session_busy"));
  return queueMode(() => activeVoiceChat
    ? Promise.reject(new Error("native_audio_session_busy")) : applyRecordMode());
}

export function setVoiceChatMode(): Promise<NativeAudioSessionInfo | null> {
  if (!isIos()) return Promise.resolve(null);
  return activeVoiceChat?.ready ?? queueMode(applyVoiceChatMode);
}

/**
 * Claim LIVE's route synchronously, before any permission/network await. Only
 * the current lease may restore playback. Releasing A after B started cannot
 * touch B, including when A's bridge operation is still pending.
 */
export function acquireVoiceChatAudioSession(): NativeVoiceChatAudioSession {
  if (!isIos()) return { ready: Promise.resolve(null), release: () => Promise.resolve(null) };
  const lease = {
    ready: Promise.resolve<NativeAudioSessionInfo | null>(null),
    release(): Promise<NativeAudioSessionInfo | null> {
      if (activeVoiceChat !== lease) return Promise.resolve(null);
      activeVoiceChat = null;
      // A newer claim may arrive before this queued restore reaches the bridge.
      return queueMode(() => activeVoiceChat ? Promise.resolve(null) : applyPlaybackMode());
    },
  };
  activeVoiceChat = lease;
  lease.ready = queueMode(() => activeVoiceChat === lease ? applyVoiceChatMode() : Promise.resolve(null));
  return lease;
}
